import React from "react";
import { motion } from "framer-motion";
import {
  CheckCircleIcon,
  EnvelopeIcon,
  PhoneIcon,
} from "@heroicons/react/24/solid";
import Button from "./ui/Button";
import Card from "./ui/Card";
import { TELEPHONE_NUMBER, COMPANY_NAME } from "../../constants/constant";

interface SuccessPageProps {
  onNewBooking: () => void;
}

const SuccessPage: React.FC<SuccessPageProps> = ({ onNewBooking }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="max-w-2xl w-full"
      >
        <Card className="p-8 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          >
            <CheckCircleIcon className="h-20 w-20 text-green-600 mx-auto mb-6" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Booking Submitted Successfully!
            </h1>
            <p className="text-gray-600 mb-8 text-lg">
              Thank you for choosing {COMPANY_NAME}. Your lesson booking has
              been received and we'll get back to you within 48 hours.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-blue-50 p-6 rounded-lg mb-8"
          >
            <h2 className="font-semibold text-blue-900 mb-4">
              What happens next?
            </h2>
            <div className="space-y-3 text-left max-w-lg mx-auto">
              <div className="flex items-start space-x-3">
                <EnvelopeIcon className="h-5 w-5 text-blue-600 mt-1 flex-shrink-0" />
                <p className="text-blue-800 text-sm">
                  Check your email for a confirmation message.
                  <span className="text-red-600 font-bold underline block mt-1">
                    (don't forget to check your spam or junk folder)
                  </span>
                </p>
              </div>
              <div className="flex items-start space-x-3">
                <PhoneIcon className="h-5 w-5 text-blue-600 mt-1 flex-shrink-0" />
                <p className="text-blue-800 text-sm">
                  Our team will contact you within 48 hours to confirm your
                  lesson details
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="space-y-4"
          >
            <Button
              onClick={onNewBooking}
              size="lg"
              className="w-full sm:w-auto"
            >
              Make Another Booking
            </Button>

            <div className="text-center text-gray-600 mt-6">
              <p className="font-semibold">Need immediate assistance?</p>
              <p className="text-sm mt-1">
                Call us at{" "}
                <a
                  href={`tel:${TELEPHONE_NUMBER}`}
                  className="font-medium hover:underline"
                >
                  {TELEPHONE_NUMBER}
                </a>
                <br />
                (7am - 8pm Japan Time)
              </p>
            </div>
          </motion.div>
        </Card>
      </motion.div>
    </div>
  );
};

export default SuccessPage;
